import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Plus, LayoutTemplate, PenLine, Pencil, Archive, ArchiveRestore, ChevronRight, Sparkles, Palette, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { PROPOSAL_STATUS_LABELS, type ProposalStatus } from "@shared/proposals";
import ProposalSheet from "@/components/Crm/ProposalSheet";
import { NewProposalDialog } from "@/components/Crm/DealProposalsPanel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import ProposalStatusBadge from "@/components/Crm/ProposalStatusBadge";
import ProposalTemplateSheet from "@/components/Crm/ProposalTemplateSheet";
import SignatureCard from "@/components/Crm/SignatureCard";

function formatBRL(raw: string | number | null | undefined, currency = "BRL"): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency });
}

const STATUS_ORDER: Array<ProposalStatus | "all"> = ["all", "pending_approval", "draft", "rejected", "approved", "sent", "accepted", "declined"];

// ─── Nova proposta a partir da página geral ──────────────────────────────────
// Toda proposta pertence a uma oportunidade (é de lá que vêm cliente, valor e
// produtos). Aqui o vendedor escolhe a oportunidade primeiro; o resto é o
// mesmo diálogo da aba Propostas do deal.
function PickDealDialog({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onPick: (dealId: number) => void;
}) {
  const deals = trpc.crm.deals.list.useQuery({ status: "open", limit: 300 }, { enabled: open });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle>Para qual oportunidade?</DialogTitle>
          <DialogDescription>
            A proposta usa o cliente, o valor e os produtos da oportunidade. Não achou? <Link href="/crm/deals" className="underline">Crie a oportunidade</Link> primeiro.
          </DialogDescription>
        </DialogHeader>
        <Command className="border-t">
          <CommandInput placeholder="Buscar por título, contato ou órgão…" autoFocus />
          <CommandList className="max-h-[360px]">
            <CommandEmpty>{deals.isLoading ? "Carregando…" : "Nenhuma oportunidade em aberto."}</CommandEmpty>
            <CommandGroup>
              {(deals.data?.rows ?? []).map((r) => {
                const contato = [r.contact?.name, r.contact?.lastName].filter(Boolean).join(" ");
                const org = r.contact?.companyName ?? "";
                return (
                  <CommandItem key={r.deal.id} value={`${r.deal.title} ${contato} ${org}`} onSelect={() => onPick(r.deal.id)} className="flex items-start gap-2 py-2">
                    <Briefcase className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.deal.title}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[org, contato].filter(Boolean).join(" · ")}{r.deal.value ? ` · ${formatBRL(r.deal.value, r.deal.currency)}` : ""}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lista de propostas ──────────────────────────────────────────────────────
function ProposalsList() {
  const { can } = usePermissions();
  const canApprove = can("crm:proposals_approve");
  const canManage = can("crm:proposals_manage");
  const [status, setStatus] = useState<ProposalStatus | "all">(canApprove ? "pending_approval" : "all");
  const [openId, setOpenId] = useState<number | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [newForDeal, setNewForDeal] = useState<number | null>(null);
  const list = trpc.crm.proposals.list.useQuery({ status: status === "all" ? undefined : status });
  const counts = trpc.crm.proposals.counts.useQuery();

  // Gestor sem nada pendente cai em "Todas" em vez de ver uma lista vazia.
  useEffect(() => {
    if (canApprove && counts.data && !counts.data.pending_approval && status === "pending_approval") setStatus("all");
  }, [counts.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = useMemo(() => Object.values(counts.data ?? {}).reduce((a, b) => a + b, 0), [counts.data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-1.5">
        {canManage && (
          <Button size="sm" className="h-8 mr-2" onClick={() => setPickOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Nova proposta
          </Button>
        )}
        {STATUS_ORDER.map((s) => {
          const n = s === "all" ? total : counts.data?.[s] ?? 0;
          const active = status === s;
          return (
            <Button key={s} size="sm" variant={active ? "default" : "outline"} className="h-8" onClick={() => setStatus(s)}>
              {s === "all" ? "Todas" : PROPOSAL_STATUS_LABELS[s]}
              <Badge variant="secondary" className={`ml-1.5 h-5 px-1.5 text-[10px] ${active ? "bg-primary-foreground/20 text-primary-foreground" : ""}`}>{n}</Badge>
            </Button>
          );
        })}
      </div>

      {list.isLoading ? (
        <div className="space-y-2"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
      ) : (list.data?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {status === "all" ? (
              <>Nenhuma proposta ainda. Clique em <strong>Nova proposta</strong> e escolha a oportunidade — ou abra a aba Propostas de uma <Link href="/crm/deals" className="underline">oportunidade</Link>.</>
            ) : (
              <>Nenhuma proposta com status “{PROPOSAL_STATUS_LABELS[status as ProposalStatus]}”.</>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {list.data!.map((row) => (
            <button
              key={row.proposal.id}
              type="button"
              onClick={() => setOpenId(row.proposal.id)}
              className="w-full text-left rounded-lg border bg-card hover:bg-accent/50 transition-colors px-4 py-3 flex items-center gap-3"
            >
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{row.proposal.number}</span>
                  <span className="font-medium truncate">{row.proposal.title}</span>
                  <ProposalStatusBadge status={row.proposal.status} />
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {row.deal.title}
                  {row.contact ? ` · ${[row.contact.name, row.contact.lastName].filter(Boolean).join(" ")}${row.contact.companyName ? ` (${row.contact.companyName})` : ""}` : ""}
                  {" · "}{formatBRL(row.proposal.totalValue, row.proposal.currency)}
                  {row.createdBy?.name ? ` · ${row.createdBy.name}` : ""}
                  {" · "}{new Date(row.proposal.updatedAt).toLocaleDateString("pt-BR")}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
      <ProposalSheet proposalId={openId} open={openId !== null} onOpenChange={(v) => !v && setOpenId(null)} />
      <PickDealDialog open={pickOpen} onOpenChange={setPickOpen} onPick={(id) => { setPickOpen(false); setNewForDeal(id); }} />
      {newForDeal !== null && (
        <NewProposalDialog
          dealId={newForDeal}
          open
          onOpenChange={(v) => !v && setNewForDeal(null)}
          onCreated={(id) => { setNewForDeal(null); setStatus("all"); setOpenId(id); }}
        />
      )}
    </div>
  );
}

// ─── Modelos ─────────────────────────────────────────────────────────────────
function TemplatesList() {
  const { can } = usePermissions();
  const canManage = can("crm:proposal_templates_manage");
  const utils = trpc.useUtils();
  const list = trpc.crm.proposalTemplates.list.useQuery({ includeInactive: true });
  const [editing, setEditing] = useState<any | null | undefined>(undefined); // undefined = fechado; null = novo
  const createDefault = trpc.crm.proposalTemplates.createDefault.useMutation({
    onSuccess: () => { toast.success("Modelo padrão criado — edite à vontade"); utils.crm.proposalTemplates.list.invalidate(); },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar modelo")),
  });
  const update = trpc.crm.proposalTemplates.update.useMutation({
    onSuccess: () => utils.crm.proposalTemplates.list.invalidate(),
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar modelo")),
  });

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setEditing(null)}><Plus className="h-4 w-4 mr-1.5" /> Novo modelo</Button>
          <Button size="sm" variant="outline" disabled={createDefault.isPending} onClick={() => createDefault.mutate()}>
            <Sparkles className="h-4 w-4 mr-1.5" /> Criar modelo padrão
          </Button>
        </div>
      )}
      {list.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : (list.data?.length ?? 0) === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          Nenhum modelo ainda. {canManage ? "Comece pelo modelo padrão e ajuste o HTML e os campos." : "Peça a um administrador para cadastrar."}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {list.data!.map((t) => (
            <div key={t.id} className={`rounded-lg border bg-card px-4 py-3 flex items-center gap-3 ${!t.isActive ? "opacity-60" : ""}`}>
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0"><LayoutTemplate className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{t.name}</span>
                  {!t.isActive && <Badge variant="outline">Arquivado</Badge>}
                  {t.engine === "canva" && <Badge className="bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 border-transparent text-[10px]"><Palette className="h-3 w-3 mr-1" /> Canva</Badge>}
                  <Badge variant="secondary" className="text-[10px]">{t.variables.filter((v) => v.kind === "ai").length} IA · {t.variables.filter((v) => v.kind === "manual").length} manuais</Badge>
                </div>
                {t.description && <div className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</div>}
              </div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" title={t.isActive ? "Arquivar" : "Reativar"} onClick={() => update.mutate({ id: t.id, isActive: !t.isActive })}>
                    {t.isActive ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ProposalTemplateSheet open={editing !== undefined} onOpenChange={(v) => !v && setEditing(undefined)} template={editing ?? null} />
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
function CrmProposalsContent() {
  const { can } = usePermissions();
  const search = useSearch();
  const [, navigate] = useLocation();
  const initialTab = new URLSearchParams(search).get("tab");
  const [tab, setTab] = useState<string>(initialTab === "templates" || initialTab === "signature" ? initialTab : "proposals");
  const counts = trpc.crm.proposals.counts.useQuery();
  const pending = counts.data?.pending_approval ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">Propostas</h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Modelo → preenchimento automático e IA → aprovação do gestor → envio ao cliente
            </p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); navigate(`/crm/proposals${v === "proposals" ? "" : `?tab=${v}`}`, { replace: true }); }}>
        <TabsList>
          <TabsTrigger value="proposals">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Propostas
            {pending > 0 && can("crm:proposals_approve") && <Badge className="ml-1.5 h-5 px-1.5 text-[10px] bg-amber-500 text-white border-transparent">{pending}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="templates"><LayoutTemplate className="h-3.5 w-3.5 mr-1.5" /> Modelos</TabsTrigger>
          {can("crm:proposals_approve") && (
            <TabsTrigger value="signature"><PenLine className="h-3.5 w-3.5 mr-1.5" /> Minha assinatura</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="proposals" className="pt-4"><ProposalsList /></TabsContent>
        <TabsContent value="templates" className="pt-4"><TemplatesList /></TabsContent>
        {can("crm:proposals_approve") && (
          <TabsContent value="signature" className="pt-4 max-w-2xl"><SignatureCard /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default function CrmProposals() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:proposals_view">
        <CrmProposalsContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
